import { useEffect, useMemo, useRef, useState } from 'react';
import { MosyUser } from '../../lib/types';
import { OwnerAvatar } from '../../lib/brandAvatars';
import { userDisplayName } from '../../lib/users';
import { Icon } from '../ui/Icon';

interface Props {
  value: number[];
  users: MosyUser[];
  onChange: (ids: number[]) => void;
  onClose: () => void;
  anchorRight?: boolean;
}

export const AssigneePicker = ({ value, users, onChange, onClose, anchorRight }: Props) => {
  const ref = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState('');

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const toggle = (id: number) => {
    if (value.includes(id)) onChange(value.filter((x) => x !== id));
    else onChange([...value, id]);
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => {
      const name = userDisplayName(u).toLowerCase();
      const email = (u.email || '').toLowerCase();
      const username = (u.username || '').toLowerCase();
      return name.includes(q) || email.includes(q) || username.includes(q);
    });
  }, [users, query]);

  return (
    <div
      ref={ref}
      className="popover"
      style={{
        top: 'calc(100% + 4px)',
        [anchorRight ? 'right' : 'left']: 0,
        minWidth: 260,
        padding: 0,
      }}
    >
      <div
        style={{
          padding: '8px 10px',
          borderBottom: '1px solid var(--border-soft)',
        }}
      >
        <input
          autoFocus
          className="input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search teammates…"
          style={{ height: 28, fontSize: 12 }}
        />
      </div>
      <div style={{ maxHeight: 280, overflowY: 'auto' }}>
        {users.length === 0 ? (
          <div style={{ padding: 10, fontSize: 12, color: 'var(--ink-4)' }}>
            No teammates yet.
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 10, fontSize: 12, color: 'var(--ink-4)' }}>
            No matches.
          </div>
        ) : (
          filtered.map((u) => {
            const selected = value.includes(u.user_id);
            const name = userDisplayName(u);
            return (
              <button
                key={u.user_id}
                type="button"
                className={`popover-item ${selected ? 'selected' : ''}`}
                onClick={() => toggle(u.user_id)}
              >
                <OwnerAvatar user={u} size={20} />
                <div style={{ flex: 1, textAlign: 'left', minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 12.5,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {name}
                  </div>
                  {u.email && (
                    <div
                      style={{
                        fontSize: 11,
                        color: 'var(--ink-4)',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {u.email}
                    </div>
                  )}
                </div>
                {selected && <Icon name="check" size={12} />}
              </button>
            );
          })
        )}
      </div>
    </div>
  );
};

export default AssigneePicker;
